import {ComponentType, lazy} from "react";

// Re-fetch a dynamically imported chunk a few times before giving up. The
// dynamic-import failures we see are dominated by transient network blips
// fetching a chunk that exists (the asset bucket retains past versions), and a
// chunk that failed to load is not cached, so an immediate re-request usually
// succeeds. A persistent failure (offline, or a hard content/proxy block)
// exhausts the retries and rejects as before, so a genuinely broken page still
// surfaces its error rather than being masked.
export async function retryImport<T>(load: () => Promise<T>, retries = 2, delayMs = 300): Promise<T> {
  try {
    return await load();
  } catch (err) {
    if (retries <= 0) {
      throw err;
    }
    await new Promise(resolve => setTimeout(resolve, delayMs));
    return retryImport(load, retries - 1, delayMs * 2);
  }
}

// `React.lazy` that retries the chunk fetch. Besides recovering transient
// failures, this matters because a plain `lazy` caches the *rejected* import
// promise: once a component's chunk fails to load, that route stays broken for
// the page's lifetime, even on navigating back, until a full reload. Retrying
// hands `lazy` a fresh attempt so a momentary failure never wedges the route.
export function lazyRetry<T extends ComponentType<any>>(load: () => Promise<{default: T}>) {
  return lazy(() => retryImport(load));
}
